import React, { useState } from 'react';
import { ChatCircle, Users } from '@phosphor-icons/react';
import ChatContainer from './ChatContainer';
import TeacherParentSelector from './TeacherParentSelector';
import GroupChatPanel from './GroupChatPanel';
import LockedFeature from '../LockedFeature';
import './ChatTabs.css';

/**
 * TeacherChat - teacher-facing messaging surface.
 *
 * Workflow: teacher picks one of their classes, sees that class's parents,
 * picks one to message. All conversation/message/polling logic lives in
 * ChatContainer — this file only supplies the teacher-specific selector
 * and labels.
 *
 * A tab switcher above it toggles to GroupChatPanel (teachers are staff, so
 * they can also join staff-to-staff groups) — a separate, self-contained
 * component with its own state. ChatContainer itself is untouched; this
 * only wraps/toggles at this level.
 */
const TeacherChat = () => {
  const [selectedClass, setSelectedClass] = useState(null);
  const [activeTab, setActiveTab] = useState('direct');

  return (
    <div className="chat-tabs-wrapper">
      <div className="chat-tabs-bar">
        <button
          className={`chat-tab-btn ${activeTab === 'direct' ? 'active' : ''}`}
          onClick={() => setActiveTab('direct')}
        >
          <ChatCircle size={16} /> Direct Messages
        </button>
        <LockedFeature module="comms_plus">
          <button
            className={`chat-tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            <Users size={16} /> Groups
          </button>
        </LockedFeature>
      </div>

      <div className="chat-tabs-content">
        {activeTab === 'direct' ? (
          <ChatContainer
            title="Messages to Parents"
            subtitle="Connect with parents in your classes"
            userRole="teacher"
            contactNoun="parent"
            contextField="class_id"
            contextValue={selectedClass}
            recentlyMessagedStorageKey="recentlyMessagedParents"
            showChangeButton
            renderSelector={({ onSelected, onCancel, recentlyMessaged }) => (
              <TeacherParentSelector
                selectedClass={selectedClass}
                onClassChange={setSelectedClass}
                onParentSelected={onSelected}
                onCancel={onCancel}
                recentlyMessaged={recentlyMessaged}
              />
            )}
          />
        ) : (
          <GroupChatPanel />
        )}
      </div>
    </div>
  );
};

export default TeacherChat;
